import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy, Download, Loader2, Mail } from 'lucide-react';
import { useI18n } from '../../../contexts/I18nContext';
import { useTeamDailyReport } from '../../../hooks/useDailyStatus';
import { downloadReport, sendTeamReport } from '../../../lib/dailyStatusApi';
import { ReportSections, SECTION_ORDER, SECTION_STYLE } from './DailyReportDrawer';

interface Props {
  date: string;
  canExportPdf: boolean;
  canSendEmail: boolean;
}

/**
 * The manager's roll-up under the "All users" cards: team totals, who has not
 * reported, every blocker with its reason, then each member's sections —
 * with copy-as-markdown, PDF and "send the team report now".
 */
export function TeamDailyReportSection({ date, canExportPdf, canSendEmail }: Props) {
  const { t } = useI18n();
  const team = useTeamDailyReport(date, true);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState<'pdf' | 'email' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const btn =
    'inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50';

  async function copyMarkdown() {
    if (!team.data) return;
    try {
      await navigator.clipboard.writeText(team.data.markdown);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setNotice({ tone: 'error', text: t('dailyStatus.drawer.copyFailed') });
    }
  }

  async function pdf() {
    setBusy('pdf');
    setNotice(null);
    try {
      await downloadReport('team', 'pdf', date);
    } catch {
      setNotice({ tone: 'error', text: t('daily.exportFailed') });
    } finally {
      setBusy(null);
    }
  }

  async function sendAll() {
    setBusy('email');
    setNotice(null);
    try {
      const res = await sendTeamReport(date);
      setNotice({
        tone: res.errors.length ? 'error' : 'ok',
        text:
          t('dailyStatus.team.sentAll', {
            individual: res.individualEmails,
            team: res.teamEmails,
          }) + (res.errors.length ? ` · ${res.errors.join('; ')}` : ''),
      });
    } catch (e) {
      setNotice({ tone: 'error', text: e instanceof Error ? e.message : t('daily.emailFailed') });
    } finally {
      setBusy(null);
    }
  }

  const data = team.data;
  const blockers = data
    ? data.members.flatMap((m) => m.sections.blocked.map((task) => ({ member: m.user, task })))
    : [];
  const notReported = data ? data.members.filter((m) => m.notReported) : [];

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-semibold text-gray-800">{t('dailyStatus.team.title')}</h2>
          {data && (
            <p className="mt-0.5 text-xs text-gray-500">
              {t('dailyStatus.team.reported', {
                reported: data.totals.reported,
                members: data.totals.members,
              })}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={copyMarkdown} disabled={!data} className={btn}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? t('dailyStatus.drawer.copied') : t('dailyStatus.drawer.copyMd')}
          </button>
          {canExportPdf && (
            <button type="button" onClick={pdf} disabled={!data || busy !== null} className={btn}>
              {busy === 'pdf' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
              {t('dailyStatus.drawer.pdf')}
            </button>
          )}
          {canSendEmail && (
            <button
              type="button"
              onClick={sendAll}
              disabled={!data || busy !== null}
              className={btn}
            >
              {busy === 'email' ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Mail size={14} />
              )}
              {t('dailyStatus.team.sendAll')}
            </button>
          )}
        </div>
      </div>
      {notice && (
        <p className={`mt-2 text-xs ${notice.tone === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}>
          {notice.text}
        </p>
      )}

      {team.isLoading && <p className="mt-3 text-sm text-gray-500">…</p>}
      {team.isError && <p className="mt-3 text-sm text-red-600">{t('dailyStatus.loadError')}</p>}

      {data && (
        <>
          <div className="mt-3 grid grid-cols-2 gap-2 text-center sm:grid-cols-5">
            {SECTION_ORDER.map((s) => (
              <div key={s} className="rounded-md bg-gray-50 py-2">
                <div className={`text-lg font-bold ${SECTION_STYLE[s].text}`}>{data.totals[s]}</div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500">
                  {t(`dailyStatus.section.${s}`)}
                </div>
              </div>
            ))}
            <div className="rounded-md bg-gray-50 py-2">
              <div className="text-lg font-bold text-gray-800">{data.totals.hoursToday}h</div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">
                {t('dailyStatus.team.hours')}
              </div>
            </div>
          </div>

          {notReported.length > 0 && (
            <p className="mt-3 text-sm text-amber-700">
              ❔ {t('dailyStatus.team.notReported')}:{' '}
              {notReported.map((m) => m.user.name).join(', ')}
            </p>
          )}

          {blockers.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-red-700">
                🚫 {t('dailyStatus.team.blocked')} ({blockers.length})
              </p>
              <ul className="mt-1 divide-y divide-gray-100 border-l-2 border-red-400 pl-3">
                {blockers.map(({ member, task }) => (
                  <li key={`${member.id}-${task.id}`} className="py-1.5 text-[13px]">
                    <span className="font-medium text-gray-800">{member.name}</span>
                    <span className="text-gray-400"> · </span>
                    {task.identifier && (
                      <span className="font-mono text-[12px] text-gray-400">
                        {task.identifier}{' '}
                      </span>
                    )}
                    <span className="text-gray-800">{task.title}</span>
                    {task.checkIn?.comment && (
                      <p className="mt-0.5 text-[12.5px] text-gray-600">{task.checkIn.comment}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-4 divide-y divide-gray-100">
            {data.members.map((member) => {
              const open = expanded[member.user.id] ?? false;
              return (
                <div key={member.user.id} className="py-2">
                  {/* Same shape for every member: phones get two fixed lines
                        1. > name ......... not reported
                        2.   ✅ 2 · 🔄 3 · 🚫 1 · ⬜ 0 · 4h
                      and sm and up one row with the counts on the right. */}
                  <button
                    type="button"
                    onClick={() => setExpanded((prev) => ({ ...prev, [member.user.id]: !open }))}
                    aria-expanded={open}
                    className="flex w-full flex-col gap-1 text-left sm:flex-row sm:items-center sm:gap-3"
                  >
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      {open ? (
                        <ChevronDown size={14} className="shrink-0 text-gray-400" aria-hidden />
                      ) : (
                        <ChevronRight size={14} className="shrink-0 text-gray-400" aria-hidden />
                      )}
                      <span className="truncate font-medium text-gray-900">{member.user.name}</span>
                      {member.notReported && (
                        <span className="ml-auto shrink-0 whitespace-nowrap text-[11px] text-amber-600 sm:ml-0">
                          ❔ {t('dailyStatus.team.notReported')}
                        </span>
                      )}
                    </span>
                    <span className="pl-[22px] text-[12px] tabular-nums text-gray-500 sm:shrink-0 sm:whitespace-nowrap sm:pl-0">
                      ✅ {member.summary.done} · 🔄 {member.summary.inProgress} · 🚫{' '}
                      {member.summary.blocked} · ⬜ {member.summary.notStarted}
                      {member.summary.hoursToday > 0 && ` · ${member.summary.hoursToday}h`}
                      {member.summary.personal > 0 &&
                        ` · ${t('dailyStatus.team.personal', { count: member.summary.personal })}`}
                    </span>
                  </button>
                  {open && (
                    <div className="pb-2">
                      {member.summary.total === 0 ? (
                        <p className="mt-2 text-sm text-gray-500">
                          {t('dailyStatus.drawer.noTasks')}
                        </p>
                      ) : (
                        <ReportSections sections={member.sections} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {data.idle.length > 0 && (
            <p className="mt-3 text-xs text-gray-500">
              {t('dailyStatus.team.idle', { names: data.idle.map((u) => u.name).join(', ') })}
            </p>
          )}
        </>
      )}
    </section>
  );
}
